import React, { createContext, useCallback, useContext, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Info,
  XCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export type ConfirmationDialogType = "danger" | "warning" | "info" | "success";

export interface ConfirmationDialogOptions {
  title: string;
  description?: string;
  type?: ConfirmationDialogType;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

interface ConfirmationDialogContextType {
  showConfirmation: (options: ConfirmationDialogOptions) => Promise<boolean>;
}

const ConfirmationDialogContext = createContext<ConfirmationDialogContextType | undefined>(undefined);

export const useConfirmationDialog = () => {
  const context = useContext(ConfirmationDialogContext);
  if (context === undefined) {
    throw new Error("useConfirmationDialog must be used within a ConfirmationDialogProvider");
  }
  return context;
};

interface ConfirmationDialogProviderProps {
  children: React.ReactNode;
}

export const ConfirmationDialogProvider: React.FC<ConfirmationDialogProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmationDialogOptions | null>(null);
  const [resolve, setResolve] = useState<((value: boolean) => void) | null>(null);

  const showConfirmation = useCallback((dialogOptions: ConfirmationDialogOptions): Promise<boolean> => {
    return new Promise((resolvePromise) => {
      setOptions(dialogOptions);
      setResolve(() => resolvePromise);
      setIsOpen(true);
    });
  }, []);

  const handleConfirm = async () => {
    if (options?.onConfirm) {
      try {
        await options.onConfirm();
      } catch (error) {
        console.error("Confirmation action failed:", error);
      }
    }
    resolve?.(true);
    setIsOpen(false);
    setOptions(null);
    setResolve(null);
  };

  const handleCancel = () => {
    options?.onCancel?.();
    resolve?.(false);
    setIsOpen(false);
    setOptions(null);
    setResolve(null);
  };

  const getIcon = (type: ConfirmationDialogType) => {
    switch (type) {
      case "danger":
        return <XCircle className="h-6 w-6 text-destructive" />;
      case "warning":
        return <AlertTriangle className="h-6 w-6 text-yellow-500" />;
      case "success":
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case "info":
      default:
        return <Info className="h-6 w-6 text-blue-500" />;
    }
  };

  const getConfirmButtonVariant = (type: ConfirmationDialogType) => {
    switch (type) {
      case "danger":
        return "destructive";
      case "warning":
        return "default";
      case "success":
        return "default";
      case "info":
      default:
        return "default";
    }
  };

  return (
    <ConfirmationDialogContext.Provider value={{ showConfirmation }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {options?.type && getIcon(options.type)}
              <DialogTitle>{options?.title}</DialogTitle>
            </div>
            {options?.description && (
              <DialogDescription>{options.description}</DialogDescription>
            )}
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex-1 sm:flex-none"
            >
              {options?.cancelText || "Cancel"}
            </Button>
            <Button
              variant={getConfirmButtonVariant(options?.type || "info")}
              onClick={handleConfirm}
              className="flex-1 sm:flex-none"
            >
              {options?.confirmText || "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmationDialogContext.Provider>
  );
}; 